/* shared, controlled field components */
import React, { useState, useRef, useEffect } from "react";
import { Icon } from "./Icon.jsx";
import { lookupAddress, lookupVehicle, fmt0 } from "../data/data.js";

/* ---- Field wrapper ---- */
export function Field({ label, required, hint, error, children, htmlFor }) {
  return (
    <div>
      {label && (
        <label className="pf-label" htmlFor={htmlFor}>
          {label}{required && <span className="req">*</span>}
        </label>
      )}
      {children}
      {error ? <div className="pf-err">{error}</div> : hint ? <div className="pf-hint">{hint}</div> : null}
    </div>
  );
}

/* ---- Text / tel / email ---- */
export function TextField({ value, onChange, placeholder, type = "text", inputMode, autoCap, error, ...rest }) {
  return (
    <input
      className={"pf-input" + (error ? " is-error" : "")}
      type={type} inputMode={inputMode} value={value || ""}
      placeholder={placeholder}
      onChange={(e) => {
        let v = e.target.value;
        if (autoCap && v) v = v.charAt(0).toUpperCase() + v.slice(1);
        onChange(v);
      }}
      {...rest}
    />
  );
}

/* ---- Currency ---- */
export function CurrencyField({ value, onChange, placeholder = "0", suffix, error }) {
  return (
    <div className="pf-input-wrap">
      <span className="pf-input-prefix">$</span>
      <input
        className={"pf-input has-prefix" + (error ? " is-error" : "")}
        type="text" inputMode="numeric"
        value={value === "" || value == null ? "" : Number(value).toLocaleString("en-AU")}
        placeholder={placeholder}
        onChange={(e) => {
          const n = e.target.value.replace(/[^0-9]/g, "");
          onChange(n === "" ? "" : Number(n));
        }}
      />
      {suffix && <span className="pf-input-suffix">{suffix}</span>}
    </div>
  );
}

/* ---- Textarea ---- */
export function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea className="pf-input" rows={rows} value={value || ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} />
  );
}

/* ---- Select ---- */
export function Select({ value, onChange, options, placeholder = "Select…" }) {
  return (
    <select className={"pf-select" + (!value ? " placeholder" : "")} value={value || ""}
      onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

/* ---- Tile single-select ---- */
export function TileSelect({ value, onChange, options, cols = 1, center = false, showCheck = true }) {
  return (
    <div className="pf-tilegrid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {options.map((o) => {
        const on = value === o.v;
        return (
          <button key={o.v} type="button"
            className={"pf-tile" + (center ? " pf-tile--center" : "") + (on ? " is-on" : "")}
            onClick={() => onChange(o.v)}>
            {o.icon && (
              <span className="pf-tile__icon"><Icon name={o.icon} size={center ? 26 : 22} /></span>
            )}
            <span className="pf-tile__body">
              <span className="pf-tile__title">{o.l}</span>
              {o.sub && <span className="pf-tile__sub">{o.sub}</span>}
            </span>
            {showCheck && !center && (
              <span className="pf-tile__check">{on && <Icon name="check" size={13} />}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ---- Tile MULTI-select — same furniture as TileSelect, but the value is an
       array and tiles toggle. For "tick all that apply" questions. ---- */
export function MultiSelect({ value, onChange, options, cols = 1 }) {
  const chosen = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(chosen.includes(v) ? chosen.filter((x) => x !== v) : [...chosen, v]);
  return (
    <div className="pf-tilegrid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {options.map((o) => {
        const on = chosen.includes(o.v);
        return (
          <button key={o.v} type="button" className={"pf-tile" + (on ? " is-on" : "")}
                  aria-pressed={on} onClick={() => toggle(o.v)}>
            {o.icon && <span className="pf-tile__icon"><Icon name={o.icon} size={22} /></span>}
            <span className="pf-tile__body">
              <span className="pf-tile__title">{o.l}</span>
              {o.sub && <span className="pf-tile__sub">{o.sub}</span>}
            </span>
            <span className="pf-tile__check">{on && <Icon name="check" size={13} />}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---- Searchable picker — a plain <select> is unusable at ~100 options
       (ANZSIC), so this is a type-to-filter list. Options are grouped by
       their `group` when present. Picking sets {v,l}; the caller stores both
       the code and the label so a later catalogue revision can't orphan it. */
export function SearchSelect({ value, label, onChange, options, placeholder = "Search…" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  // Match the label, the group, or the option's own keywords — so someone
  // types what they DO ("plumbing") and lands on the right category.
  const needle = q.trim().toLowerCase();
  const hits = (needle
    ? options.filter((o) => o.l.toLowerCase().includes(needle)
      || (o.group || "").toLowerCase().includes(needle)
      || (o.kw || "").includes(needle))
    : options).slice(0, 60);

  if (value && !open) {
    return (
      <div className="pf-picked" ref={boxRef}>
        <span className="pf-picked__t">{label || value}</span>
        <button type="button" className="pf-picked__x" onClick={() => { setQ(""); setOpen(true); }} aria-label="Change selection">
          <Icon name="close" size={15} />
        </button>
      </div>
    );
  }
  return (
    <div className="pf-searchsel" ref={boxRef}>
      <input className="pf-input" value={q} placeholder={placeholder} autoComplete="off"
             onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      {open && (
        <div className="pf-searchsel__list" role="listbox">
          {hits.length === 0 && <div className="pf-searchsel__empty">No matches - try another word.</div>}
          {hits.map((o) => (
            <button key={o.v} type="button" className="pf-searchsel__opt" role="option" aria-selected={value === o.v}
                    onClick={() => { onChange(o); setOpen(false); setQ(""); }}>
              <span>{o.l}</span>
              {o.group && <span className="pf-searchsel__grp">{o.group}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Yes / No ---- */
export function YesNo({ value, onChange, yesLabel = "Yes", noLabel = "No" }) {
  const opt = (val, label) => {
    const on = value === val;
    return (
      <button type="button" className={"pf-tile pf-tile--center" + (on ? " is-on" : "")}
        onClick={() => onChange(val)} style={{ minHeight: 64 }}>
        <span className="pf-tile__title">{label}</span>
      </button>
    );
  };
  return (
    <div className="pf-tilegrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
      {opt(true, yesLabel)}{opt(false, noLabel)}
    </div>
  );
}

/* ---- Toggle switch ---- */
export function Toggle({ value, onChange, labelOn = "Yes", labelOff = "No" }) {
  return (
    <button type="button" onClick={() => onChange(!value)} style={{
      display: "inline-flex", alignItems: "center", gap: 12,
    }}>
      <span style={{
        width: 52, height: 30, borderRadius: 99, padding: 3, flexShrink: 0,
        background: value ? "var(--coral)" : "var(--field-2)", transition: "background .2s",
        display: "flex", justifyContent: value ? "flex-end" : "flex-start",
      }}>
        <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.2)", transition: "all .2s" }} />
      </span>
      <span style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-70)" }}>{value ? labelOn : labelOff}</span>
    </button>
  );
}

/* ---- Money slider ---- */
export function MoneySlider({ value, onChange, min, max, step = 500, format = fmt0, sublabel }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 34, letterSpacing: "-.02em" }}>{format(value)}</span>
        {sublabel && <span style={{ fontSize: 14, color: "var(--ink-45)" }}>{sublabel}</span>}
      </div>
      <input className="pf-slider" type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ background: `linear-gradient(90deg, var(--coral) ${pct}%, var(--field-2) ${pct}%)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: "var(--ink-45)", fontWeight: 500 }}>
        <span>{format(min)}</span><span>{format(max)}</span>
      </div>
    </div>
  );
}

/* ---- Stepper ---- */
export function Stepper({ value, onChange, min = 0, max = 20, suffix }) {
  return (
    <div className="pf-stepper">
      <button className="pf-stepper__btn" type="button" disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} aria-label="decrease"><Icon name="minus" size={22} /></button>
      <span className="pf-stepper__val">{value}{suffix ? <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-45)", marginLeft: 4 }}>{suffix}</span> : null}</span>
      <button className="pf-stepper__btn" type="button" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} aria-label="increase"><Icon name="plus" size={22} /></button>
    </div>
  );
}

/* ---- Address autocomplete (mock Google Places) ---- */
export function AddressField({ value, onChange, placeholder = "Start typing your address…", error }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [sugs, setSugs] = useState([]);
  const wrap = useRef(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const onType = (v) => { setQ(v); onChange(v); const s = lookupAddress(v); setSugs(s); setOpen(s.length > 0 && v.length >= 2); };
  return (
    <div className="pf-input-wrap" ref={wrap} style={{ position: "relative" }}>
      <span className="pf-input-prefix" style={{ pointerEvents: "none" }}><Icon name="pin" size={18} style={{ color: "var(--ink-45)" }} /></span>
      <input className={"pf-input" + (error ? " is-error" : "")} style={{ paddingLeft: 44 }}
        value={q} placeholder={placeholder}
        onChange={(e) => onType(e.target.value)} onFocus={() => { if (sugs.length) setOpen(true); }} />
      {open && (
        <div className="pf-card pf-rise" style={{ position: "absolute", top: 60, left: 0, right: 0, zIndex: 40, boxShadow: "var(--sh-pop)", padding: 6, animationDuration: ".2s" }}>
          {sugs.map((s, i) => (
            <button key={i} type="button" onClick={() => { setQ(s); onChange(s); setOpen(false); }}
              style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left", padding: "11px 12px", borderRadius: 12, color: "var(--ink)" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--field)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
              <Icon name="pin" size={17} style={{ color: "var(--coral)", flexShrink: 0 }} />
              <span style={{ fontSize: 14.5 }}>{s}</span>
            </button>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 8px 2px" }}>
            <span style={{ fontSize: 10, color: "var(--ink-25)", fontWeight: 600, letterSpacing: ".04em" }}>POWERED BY GOOGLE</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Rego lookup (mock AutoGrab) ---- */
export function RegoField({ rego, vehicle, onRego, onVehicle, error }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const timer = useRef(null);
  const trigger = (val) => {
    onRego(val); setNotFound(false); setDone(false); onVehicle(null);
    clearTimeout(timer.current);
    const clean = val.replace(/\s/g, "");
    if (clean.length >= 6) {
      setLoading(true);
      timer.current = setTimeout(async () => {
        const v = await lookupVehicle(clean);
        setLoading(false);
        if (v) { onVehicle(v); setDone(true); } else { setNotFound(true); }
      }, 200);
    } else { setLoading(false); }
  };
  return (
    <div>
      <div className="pf-input-wrap">
        <input className={"pf-input" + (error ? " is-error" : "")} style={{ textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, paddingRight: 48 }}
          value={rego || ""} placeholder="e.g. ABC123" maxLength={8}
          onChange={(e) => trigger(e.target.value.toUpperCase())} />
        <span className="pf-input-suffix">
          {loading ? <span className="pf-spin" style={{ display: "block", width: 18, height: 18, border: "2.5px solid var(--field-2)", borderTopColor: "var(--coral)", borderRadius: "50%" }} />
            : done ? <Icon name="check" size={20} style={{ color: "var(--success)" }} /> : <Icon name="search" size={18} style={{ color: "var(--ink-45)" }} />}
        </span>
      </div>
      {loading && (
        <div className="pf-card" style={{ marginTop: 12, padding: 16, display: "flex", gap: 14, alignItems: "center" }}>
          <div className="pf-skeleton" style={{ width: 56, height: 56, borderRadius: 12 }} />
          <div style={{ flex: 1 }}>
            <div className="pf-skeleton" style={{ height: 15, width: "62%", marginBottom: 9 }} />
            <div className="pf-skeleton" style={{ height: 12, width: "40%" }} />
          </div>
        </div>
      )}
      {vehicle && done && (
        <div className="pf-card pf-rise" style={{ marginTop: 12, padding: 16, display: "flex", gap: 14, alignItems: "center", borderColor: "var(--success)", background: "var(--success-tint)" }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink)", flexShrink: 0 }}>
            <Icon name="car" size={30} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>{vehicle.year} {vehicle.make} {vehicle.model}</div>
            <div style={{ fontSize: 13, color: "var(--ink-45)" }}>{vehicle.colour} · {vehicle.bodyType} · est. {fmt0(vehicle.value)}</div>
          </div>
          <Icon name="check" size={22} style={{ color: "var(--success)", flexShrink: 0 }} />
        </div>
      )}
      {notFound && (
        <div style={{ marginTop: 12, fontSize: 14, color: "var(--ink-70)" }}>
          We couldn't find that plate. <button type="button" className="pf-btn--text" style={{ display: "inline", padding: 0, fontSize: 14 }} onClick={() => { onVehicle({ manual: true, make: "", model: "", year: "" }); setNotFound(false); setDone(true); }}>Enter details manually →</button>
        </div>
      )}
    </div>
  );
}
